import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { timeEntriesAPI, timeLookupsAPI } from '../services/api';
import { TimeEntry, TimeProfile, TimeProjectLookup, TimeProjectTaskLookup } from '../types';
import { minutesToParts, partsToMinutes } from '../utils';
import { extractBadRequestErrorMessage } from '../utils/apiError';
import DateInput from './DateInput';
import TimeSelect from './TimeSelect';

interface Props {
  entry: TimeEntry;
  profiles: TimeProfile[];
  projects: TimeProjectLookup[];
  onClose: () => void;
  onSaved: () => void;
}

const ReportEntryEditDialog: React.FC<Props> = ({ entry, profiles, projects, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    timeProfileId: entry.timeProfileId,
    projectId: entry.projectId || 0,
    projectTaskId: entry.projectTaskId || 0,
    workDate: entry.workDate.split('T')[0],
    ...minutesToParts(entry.durationMinutes),
    isBillable: entry.isBillable,
    description: entry.description || '',
  }));
  const [tasks, setTasks] = useState<TimeProjectTaskLookup[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [lookupError, setLookupError] = useState('');
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const saveGuard = useRef(false);
  const dialog = useRef<HTMLElement>(null);

  useEffect(() => {
    let active = true;
    setLoadingTasks(true);
    setLookupError('');
    timeLookupsAPI.getProjectTasks().then(data => {
      if (active) setTasks(data);
    }).catch(() => {
      if (active) setLookupError('Tasks could not be loaded. Try again.');
    }).finally(() => { if (active) setLoadingTasks(false); });
    return () => { active = false; };
  }, [lookupAttempt]);

  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => { if (trigger?.isConnected) trigger.focus(); };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saveGuard.current || loadingTasks || lookupError) return;
    const durationMinutes = partsToMinutes(form.hours, form.minutes);
    if (!form.timeProfileId || !form.projectId || !form.workDate || !Number.isInteger(durationMinutes) || durationMinutes < 1 || durationMinutes > 1440) {
      setError('Select a member, date and project, and enter between 1 and 1440 minutes.');
      return;
    }
    saveGuard.current = true;
    setSaving(true);
    setError('');
    try {
      await timeEntriesAPI.updateEntry(entry.id, {
        id: entry.id,
        timeProfileId: form.timeProfileId,
        projectId: form.projectId,
        projectTaskId: form.projectTaskId || undefined,
        workDate: form.workDate,
        durationMinutes,
        isBillable: form.isBillable,
        description: form.description || undefined,
      });
    } catch (saveError) {
      setError(extractBadRequestErrorMessage(saveError, 'Entry could not be saved'));
      return;
    } finally {
      saveGuard.current = false;
      setSaving(false);
    }
    onSaved();
  };

  const memberOptions = profiles.filter(profile => profile.isActive || profile.id === entry.timeProfileId)
    .map(profile => ({ value: profile.id, label: profile.displayName }));
  if (!memberOptions.some(option => option.value === entry.timeProfileId)) {
    memberOptions.push({ value: entry.timeProfileId, label: entry.timeProfileName });
  }
  const projectOptions = projects.map(project => ({ value: project.id, label: `${project.customerTitle} - ${project.title}` }));
  if (entry.projectId && !projectOptions.some(option => option.value === entry.projectId)) {
    projectOptions.push({ value: entry.projectId, label: entry.projectTitle || 'Current project' });
  }
  const taskOptions = tasks.filter(task => task.projectId === form.projectId).map(task => ({ value: task.id, label: task.title }));
  if (form.projectId === entry.projectId && entry.projectTaskId && !taskOptions.some(option => option.value === entry.projectTaskId)) {
    taskOptions.push({ value: entry.projectTaskId, label: entry.projectTaskTitle || 'Current task' });
  }

  return <div className="time-modal-backdrop" onMouseDown={event => {
    if (event.target === event.currentTarget && !saveGuard.current) onClose();
  }}>
    <section ref={dialog} className="time-modal entry-modal entry-modal-wide" role="dialog" aria-modal="true"
      aria-labelledby="report-entry-title" tabIndex={-1} onKeyDown={event => {
        if (event.key === 'Escape' && !saveGuard.current) { event.preventDefault(); onClose(); }
        if (event.key !== 'Tab') return;
        const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled)') || []);
        const first = controls[0]; const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); dialog.current?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first.focus(); }
      }}>
      <div className="time-modal-header"><h2 id="report-entry-title" className="card-title">Edit entry</h2></div>
      <div className="time-modal-body">
        <form className="time-form entry-form" onSubmit={submit}>
          <div className="form-group">
            <label className="form-label" htmlFor="report-entry-member">Member</label>
            <TimeSelect inputId="report-entry-member" ariaLabel="Member" options={memberOptions} value={form.timeProfileId}
              isDisabled={saving} onChange={value => setForm(prev => ({ ...prev, timeProfileId: Number(value || 0) }))} />
          </div>
          <div className="entry-meta-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="report-entry-date">Date</label>
              <DateInput id="report-entry-date" className="form-control" strict required disabled={saving}
                value={form.workDate} onChange={value => setForm(prev => ({ ...prev, workDate: value }))} />
            </div>
            <div className="duration-grid entry-duration-grid">
              <div className="form-group">
                <label className="form-label" htmlFor="report-entry-hours">Hours</label>
                <input id="report-entry-hours" className="form-control" type="number" min="0" max="24" disabled={saving}
                  value={form.hours} onChange={event => setForm(prev => ({ ...prev, hours: Number(event.target.value) }))} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="report-entry-minutes">Minutes</label>
                <input id="report-entry-minutes" className="form-control" type="number" min="0" max="59" disabled={saving}
                  value={form.minutes} onChange={event => setForm(prev => ({ ...prev, minutes: Number(event.target.value) }))} />
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="report-entry-project">Project</label>
            <TimeSelect inputId="report-entry-project" ariaLabel="Project" options={projectOptions} value={form.projectId || null}
              isDisabled={saving} onChange={value => setForm(prev => ({ ...prev, projectId: Number(value || 0), projectTaskId: 0 }))} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="report-entry-task">Task</label>
            <TimeSelect inputId="report-entry-task" ariaLabel="Task" options={taskOptions} value={form.projectTaskId || null}
              isClearable isDisabled={saving || loadingTasks || !form.projectId}
              onChange={value => setForm(prev => ({ ...prev, projectTaskId: Number(value || 0) }))} />
            {loadingTasks && <p role="status">Loading tasks...</p>}
            {lookupError && <p role="alert">{lookupError} <button className="btn btn-secondary btn-sm" type="button" onClick={() => setLookupAttempt(prev => prev + 1)}>Retry tasks</button></p>}
          </div>
          <label className="toggle-row entry-toggle-row">
            <input type="checkbox" checked={form.isBillable} disabled={saving}
              onChange={event => setForm(prev => ({ ...prev, isBillable: event.target.checked }))} /> Billable
          </label>
          <div className="form-group entry-description-field">
            <label className="form-label" htmlFor="report-entry-description">Description</label>
            <textarea id="report-entry-description" className="form-control entry-description-control" rows={5} maxLength={2000}
              disabled={saving} value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
          </div>
          {error && <p role="alert" className="alert alert-danger">{error}</p>}
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={saving || loadingTasks || !!lookupError} aria-busy={saving}>{saving ? 'Saving entry...' : 'Save'}</button>
            <button className="btn btn-secondary" type="button" disabled={saving} onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </section>
  </div>;
};

export default ReportEntryEditDialog;
